import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Package } from 'lucide-react';

const FALLBACK_SELLING_PLANS = {
  rush: [
    {
      id: 'gid://shopify/SellingPlan/3596222649',
      name: 'Deliver every month',
      options: [{ name: 'Frequency', value: 'Deliver every month' }],
      priceAdjustments: [{ adjustmentValue: { adjustmentPercentage: 10 } }],
    },
  ],
  synaptiq: [
    {
      id: 'gid://shopify/SellingPlan/3596222649',
      name: 'Deliver every month',
      options: [{ name: 'Frequency', value: 'Deliver every month' }],
      priceAdjustments: [{ adjustmentValue: { adjustmentPercentage: 10 } }],
    },
  ],
  vitra: [
    {
      id: 'gid://shopify/SellingPlan/3596222649',
      name: 'Deliver every month',
      options: [{ name: 'Frequency', value: 'Deliver every month' }],
      priceAdjustments: [{ adjustmentValue: { adjustmentPercentage: 10 } }],
    },
  ],
};

const SubscriptionSelector = ({ 
  product, 
  purchaseType = 'onetime',
  selectedSellingPlanId = null,
  onPurchaseTypeChange,
  sellingPlans = []
}) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Reset state when component mounts or product changes
  useEffect(() => {
    setSelectedPlan(null);
    onPurchaseTypeChange('onetime', null);
  }, [product?.id, onPurchaseTypeChange]);
  
  const availablePlans = useMemo(() => {
    const apiPlans = sellingPlans.flatMap((planGroup) =>
      planGroup?.node?.sellingPlans?.edges?.map((plan) => plan.node) || []
    );

    return apiPlans.length > 0 ? apiPlans : FALLBACK_SELLING_PLANS[product?.id] || [];
  }, [product?.id, sellingPlans]);
  const hasSellingPlans = availablePlans.length > 0;
  
  // Extract discount from first selling plan
  const firstPlan = availablePlans[0];
  const discountPercentage = firstPlan?.priceAdjustments?.[0]?.adjustmentValue?.adjustmentPercentage || 10;

  useEffect(() => {
    if (!selectedSellingPlanId || !hasSellingPlans) {
      setSelectedPlan(null);
      return;
    }

    const matchingPlan = availablePlans.find((plan) => plan.id === selectedSellingPlanId);

    setSelectedPlan(matchingPlan || null);
  }, [availablePlans, hasSellingPlans, selectedSellingPlanId]);
  
  const handlePurchaseTypeChange = (type) => {
    if (type === 'subscription') {
      if (hasSellingPlans) {
        const defaultPlan = availablePlans[0];
        setSelectedPlan(defaultPlan);
        onPurchaseTypeChange(type, defaultPlan?.id);
      } else {
        setSelectedPlan(null);
        onPurchaseTypeChange('onetime', null);
      }
    } else {
      setSelectedPlan(null);
      onPurchaseTypeChange(type, null);
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-4 text-gray-900">Purchase Options</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* One-time purchase option */}
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePurchaseTypeChange('onetime');
          }}
          className={`
            relative p-4 rounded-xl border-2 transition-all
            ${purchaseType === 'onetime' 
              ? 'border-berleto-blue bg-berleto-blue/5' 
              : 'border-gray-200 hover:border-gray-300 bg-white'
            }
          `}
          style={{ pointerEvents: 'auto' }}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-berleto-blue" />
                <span className="font-semibold text-gray-900">One-Time Purchase</span>
              </div>
              <p className="text-sm text-gray-600">
                Single delivery, no commitment
              </p>
            </div>
            {purchaseType === 'onetime' && (
              <div className="w-6 h-6 rounded-full bg-berleto-blue flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        </motion.button>

        {/* Subscription option */}
        <motion.button
          type="button"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePurchaseTypeChange('subscription');
          }}
          disabled={!hasSellingPlans}
          className={`
            relative p-4 rounded-xl border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed
            ${purchaseType === 'subscription' 
              ? 'border-berleto-green bg-berleto-green/5' 
              : 'border-gray-200 hover:border-gray-300 bg-white'
            }
          `}
          style={{ pointerEvents: 'auto' }}
        >
          {/* Discount Badge */}
          <div className="absolute -top-2 -right-2 bg-berleto-green text-white px-2 py-1 rounded-full text-xs font-bold">
            -{discountPercentage}%
          </div>
          
          <div className="flex items-start justify-between">
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-berleto-green" />
                <span className="font-semibold text-gray-900">Subscribe & Save</span>
              </div>
              <p className="text-sm text-gray-600">
                {hasSellingPlans ? `Regular delivery with ${discountPercentage}% discount` : 'Subscription unavailable for this variant'}
              </p>
              {hasSellingPlans && (
                <p className="text-xs text-berleto-green mt-1 font-medium">
                  Cancel anytime
                </p>
              )}
            </div>
            {purchaseType === 'subscription' && (
              <div className="w-6 h-6 rounded-full bg-berleto-green flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        </motion.button>
      </div>

      {/* Delivery interval options for subscription */}
      {purchaseType === 'subscription' && hasSellingPlans && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-4 p-4 bg-gray-50 rounded-xl"
        >
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Delivery frequency:</h4>
          <div className="grid grid-cols-1 gap-2">
            {availablePlans.map((planNode, planIndex) => {
                const planName = planNode.name || planNode.options?.[0]?.value || 'Monthly';
                
                return (
                  <button
                    key={planNode.id || planIndex}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedPlan(planNode);
                      onPurchaseTypeChange('subscription', planNode.id);
                    }}
                    className={`
                      p-3 rounded-lg border text-sm transition-all
                      ${selectedPlan?.id === planNode.id
                        ? 'border-berleto-green bg-berleto-green/10 text-berleto-green font-medium'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    {planName}
                    {planNode.priceAdjustments?.[0]?.adjustmentValue?.adjustmentPercentage && (
                      <span className="ml-2 text-xs">
                        (Save {planNode.priceAdjustments[0].adjustmentValue.adjustmentPercentage}%)
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        </motion.div>
      )}

      {/* Subscription benefits */}
      {purchaseType === 'subscription' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 bg-berleto-green/5 p-4 rounded-xl"
        >
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Your Subscription Benefits:</h4>
          <ul className="space-y-1 text-sm text-gray-600">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-berleto-green" />
              <span>{discountPercentage}% discount on every order</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-berleto-green" />
              <span>Free USA shipping on all deliveries</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-berleto-green" />
              <span>Pause or cancel anytime</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-berleto-green" />
              <span>Exclusive subscriber offers</span>
            </li>
          </ul>
        </motion.div>
      )}
    </div>
  );
};

export default SubscriptionSelector;
